import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { supportChatService, Conversation, Message } from '../../../services/supportChatService';
import { socketService } from '../../../services/socketService';
import { useToast } from '../../../components/Toast';

export const useSupportChat = () => {
    const { user } = useAuth();
    const { showToast } = useToast();

    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [currentTime, setCurrentTime] = useState(Date.now());

    // Socket Connection Management
    useEffect(() => {
        if (!socketService.isConnected()) {
            socketService.connect();
        }

        const handleSocketConnect = () => {
            setTimeout(() => loadConversations(), 1500);
        };

        const socket = socketService.socket;
        socket?.on('connect', handleSocketConnect);

        loadConversations();

        const heartbeatInterval = setInterval(() => {
            supportChatService.sendHeartbeat();
        }, 20000);

        const timeInterval = setInterval(() => {
            setCurrentTime(Date.now());
        }, 30000);

        return () => {
            clearInterval(heartbeatInterval);
            clearInterval(timeInterval);
            socket?.off('connect', handleSocketConnect);
        };
    }, []);

    // Events
    useEffect(() => {
        const unsubscribeNewMessage = supportChatService.onNewMessage((message: Message) => {
            setConversations(prev => {
                return prev.map(conv => {
                    if (conv.id === message.conversation_id) {
                        return {
                            ...conv,
                            last_message: message,
                            last_message_at: message.created_at,
                            unread_count: (selectedConversation?.id !== message.conversation_id && message.sender_id !== user?.id)
                                ? (conv.unread_count || 0) + 1
                                : conv.unread_count,
                        };
                    }
                    return conv;
                }).sort((a, b) => {
                    const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
                    const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
                    return bTime - aTime;
                });
            });

            if (selectedConversation && message.conversation_id === selectedConversation.id) {
                setMessages(prev => {
                    if (prev.some(m => m.id === message.id)) return prev;
                    return [...prev, message];
                });
                if (message.sender_id !== user?.id) {
                    supportChatService.markMessageRead(message.id).catch(console.warn);
                }
            }
        });

        // Other status updates omitted for brevity, similar to useChat but for supportChatService events if they exist
        // Assuming supportChatService has similar event updates

        return () => {
            unsubscribeNewMessage();
        };
    }, [selectedConversation, user?.id]);

    const loadConversations = useCallback(async () => {
        try {
            setLoading(true);
            const data = await supportChatService.getConversations();
            if (Array.isArray(data)) {
                setConversations(data.map(c => ({
                    ...c,
                    other_user: { ...c.other_user, is_online: c.other_user?.is_online === true }
                })));

                // Auto create chat for normal user if empty
                if (user?.role !== 'admin' && data.length === 0 && !selectedConversation) {
                    createOrGetSupportConversation();
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [user?.role, selectedConversation]);

    const createOrGetSupportConversation = async () => {
        try {
            const conversation = await supportChatService.createOrGetConversation();
            setConversations(prev => {
                if (prev.some(c => c.id === conversation.id)) return prev;
                return [conversation, ...prev];
            });
            if (!selectedConversation) setSelectedConversation(conversation);
            return conversation;
        } catch (error) {
            console.error(error);
        }
    };

    const loadMessages = async (conversationId: number) => {
        try {
            setLoadingMessages(true);
            const data = await supportChatService.getMessages(conversationId);
            setMessages(data.messages);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingMessages(false);
        }
    };

    const selectConversation = useCallback((conversation: Conversation) => {
        setSelectedConversation(conversation);
        loadMessages(conversation.id);
        if (socketService.isConnected()) {
            supportChatService.joinConversation(conversation.id);
            supportChatService.markAllRead(conversation.id).catch(console.warn);
        }
    }, []);

    const sendMessage = async (content: string, files?: { file?: File, video?: File, link?: string }) => {
        if (!selectedConversation) return;
        try {
            const newMessage = await supportChatService.sendMessage(
                selectedConversation.id,
                content,
                {
                    attachment: files?.file,
                    video: files?.video,
                    linkUrl: files?.link
                }
            );
            setMessages(prev => [...prev, newMessage]);
            return newMessage;
        } catch (error) {
            console.error(error);
            showToast('Failed to send message', 'error');
            throw error;
        }
    };

    return {
        conversations,
        selectedConversation,
        messages,
        loading,
        loadingMessages,
        selectConversation,
        sendMessage,
        currentTime
    };
};
